import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import emailjs from "emailjs-com";

const GOOGLE_FORM_URL =
  "https://forms.gle/iVYJmNvr7HTuhX7D6";

// 🚀 Enhanced Question Flow
const QUESTION_FLOW = [
  {
    id: "rental",
    title: "EV Rentals / Plans ⚡",
    followUps: [
      { id: "city", q: "Which city do you need an EV rental in?", type: "options", options: ["Bengaluru", "Chennai", "Hyderabad", "Mumbai", "Delhi", "Other"] },
      { id: "duration", q: "How long do you plan to rent it?", type: "options", options: [ "1-month", "> 1-month", "< 1-month"] },
      { id: "vehicle", q: "Choose your preferred vehicle type:", type: "options", options: ["2 Wheeler"] },
    ],
  },
  {
    id: "partner",
    title: "Join as Delivery Partner 🛵",
    followUps: [
      { id: "area", q: "Which city are you operating in?", type: "options", options: ["Bengaluru", "Chennai", "Hyderabad", "Mumbai", "Delhi", "Other"] },
      { id: "vehicle_owned", q: "Do you have your own vehicle?", type: "options", options: ["Yes", "No"] },
      { id: "docs", q: "Do you have a valid driver’s license and ID proof?", type: "options", options: ["Yes", "No"] },
    ],
  },
  {
    id: "investor",
    title: "Investor / Franchise Enquiry 💼",
    followUps: [
      { id: "type", q: "Are you an individual or corporate investor?", type: "options", options: ["Individual", "Corporate"] },
      { id: "budget", q: "What’s your expected investment range?", type: "options", options: ["< ₹5cr", "₹5cr - ₹25cr", "> ₹25cr", "< ₹25cr"] },
      { id: "city", q: "Which city would you like to set up in?", type: "text" },
    ],
  },
   {
    id: "fleet",
    title: "Fleet / Logistics Solutions 🛵",
    followUps: [
      { id: "num", q: "How many EVs are you looking to onboard?", type: "options", options: ["10-20", "41-70", "> 100"] },
      { id: "usage", q: "What type of business do you manage?", type: "options", options: ["Food Delivery", "Courier", "Logistics", "Other"] },
      { id: "routes", q: "Mention your major delivery routes or zones.", type: "text" },
    ],
  },
  {
    id: "other",
    title: "Others / General Help 💬",
    followUps: [
      { id: "custom", q: "Please type your question below:", type: "text" },
    ],
  },
];

// Add FAQ data at the top with existing constants
const FAQ_DATA = {
  // ...existing QUESTION_FLOW...

  // Add FAQ responses
  responses: {
    "rental": {
      default: "You can rent an EV by filling out our quick form. Would you like to proceed?",
      followup: "Great! Please fill out this form and we'll get back to you shortly."
    },
    "partner": {
      default: "Become an EV91 delivery partner in just a few steps! Ready to begin?",
      followup: "Excellent! Let's get you started with the registration form."
    },
    "fleet": {
      default: "We offer complete fleet management solutions. Here's what you need to know:",
      details: [
        "- Flexible fleet sizes (1 to 100+ vehicles)",
        "- Full maintenance support",
        "- Real-time tracking",
        "- Custom solutions for your business"
      ]
    },
    text_response: "Thank you for your input. Our team will analyze your requirements and get back to you soon. Would you like to proceed with the detailed form?",
  }
};

const FAQ_LIST = [
  { q: "What services does EV91 offer?", a: "EV91 offers electric vehicle rentals, last-mile delivery, 3PL logistics, fleet management, and EV financing for businesses and gig workers." },
  { q: "How can I rent an EV from EV91?", a: "You can rent an EV by clicking 'Get EV⚡' on our website or filling out the enquiry form on the Contact Us page." },
  { q: "Where are EV91 hubs located?", a: "Our hubs are in Bengaluru, Chennai, Hyderabad, and New Delhi. Visit our Contact Us page for addresses and directions." },
  { q: "What are EV91's business hours?", a: "We are available Monday to Saturday, 10:00 AM to 6:30 PM IST." },
  { q: "How do I contact EV91 for support?", a: "Email info@ev91riderz.com or call ‪+91 90809 73764‬. You can also reach us via WhatsApp or Instagram." },
  { q: "Is EV91 hiring?", a: "Yes! Visit the Life @ Voice page to see current openings and join our mission." },
  { q: "Can I get EV financing?", a: "We offer EV financing and subscription options. Fill out the enquiry form or contact us for details." },
  { q: "What is 3PL?", a: "3PL stands for Third-Party Logistics. EV91 provides scalable 3PL solutions including warehousing, fulfillment, and last-mile delivery." },
  { q: "How do I become a delivery partner?", a: "Go to the Life @ Voice page and apply, or click 'Get EV⚡' to start your journey as a delivery partner." },
  { q: "What types of EVs do you offer?", a: "We offer a range of electric 2-wheelers and 3-wheelers suitable for delivery, logistics, and personal use." },
  { q: "Do you provide maintenance for EVs?", a: "Yes, we provide regular maintenance and support for all our EVs to ensure smooth operations." },
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("intro");
  const [selectedMain, setSelectedMain] = useState(null);
  const [followIndex, setFollowIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [typing, setTyping] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true); // NEW
  const [messages, setMessages] = useState([]); // NEW
  const messagesEndRef = useRef(null); // NEW

  const mainOptions = QUESTION_FLOW.map((q) => ({ id: q.id, title: q.title }));
  const currentFollowUp =
    selectedMain && QUESTION_FLOW.find((f) => f.id === selectedMain).followUps[followIndex];

  useEffect(() => {
    if (stage === "followup") {
      setTyping(true);
      const timeout = setTimeout(() => setTyping(false), 800);
      return () => clearTimeout(timeout);
    }
  }, [followIndex, stage]);

  // Show welcome only once per open
  useEffect(() => {
    if (open) {
      setShowWelcome(true);
      setTimeout(() => setShowWelcome(false), 3800); // Show for 4.8 seconds
    }
  }, [open]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [followIndex, stage, messages]); // <-- Add messages as a dependency

  function resetConversation() {
    setStage("intro");
    setSelectedMain(null);
    setFollowIndex(0);
    setResponses({});
    setMessages([]); // NEW
  }

  function handleSelectMain(option) {
    setSelectedMain(option);
    setStage("followup");
    setFollowIndex(0);
  }

  function handleAnswer(followId, value) {
    setResponses((prev) => ({ ...prev, [followId]: value }));

    const followUps = QUESTION_FLOW.find((f) => f.id === selectedMain).followUps;

    // If it's a text input, show response
    if (currentFollowUp?.type === "text") {
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        // If investor, go to impress stage
        if (selectedMain === "investor") {
          setStage("impress");
        } else {
          setStage("done");
        }
      }, 1000);
      return;
    }

    if (followIndex + 1 < followUps.length) {
      setFollowIndex((i) => i + 1);
    } else {
      // If investor, go to impress stage
      if (selectedMain === "investor") {
        setStage("impress");
      } else {
        setStage("done");
      }
    }

    // Add message to chat
    setMessages((prev) => [
      ...prev,
      { text: value, isUser: true },
      { text: currentFollowUp.q, isUser: false },
    ]);
  }

  function openForm() {
    window.open(GOOGLE_FORM_URL, "_blank");
  }

  return (
    <div>
      <div className="fixed bottom-24 right-6 z-40">
        {/* Floating Icon */}
        <AnimatePresence>
          {!open && (
            <>
              {/* Desktop Button */}
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                whileHover={{ scale: 1.05 }}
                onClick={() => setOpen(true)}
                className="ev91-chat-launch hidden sm:flex relative w-72 h-16 rounded-[1.35rem] text-white items-center justify-center gap-3 group overflow-hidden"
              >
                {/* Enhanced glowing effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-lime-300/10 to-white/0 animate-pulse"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                
                {/* Animated border */}
                <div className="absolute inset-0 rounded-[1.35rem] border border-lime-200/30 animate-pulse"></div>
                
                {/* Icon with enhanced animation */}
                <div className="relative z-10 p-2 rounded-2xl bg-white/10 backdrop-blur-sm group-hover:bg-white/20 transition-all duration-300">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5 group-hover:scale-110 transition-transform duration-300" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4-.84L3 20l1.16-4.39A8 8 0 113 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                    />
                  </svg>
                </div>
                
                {/* Text with animation */}
                <span className="text-lg font-black tracking-wide transition-all duration-300">
                  EV91 Assistant
                </span>

                {/* Enhanced notification dot */}
                <div className="absolute -top-1 -right-1 w-4 h-4">
                  <div className="absolute inset-0 rounded-full bg-green-400 animate-pulse"></div>
                  <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></div>
                </div>
              </motion.button>

              {/* Mobile Circle Button */}
              <motion.button
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                whileTap={{ scale: 0.93 }}
                onClick={() => setOpen(true)}
                className="ev91-chat-launch sm:hidden flex items-center justify-center w-16 h-16 rounded-full fixed bottom-24 right-6 z-[9999]"
                style={{
                  boxShadow: "0 22px 55px rgba(15, 107, 74, 0.32)",
                }}
              >
                <span className="text-lg font-black">EV</span>
                {/* You can use 🤖 or any emoji/icon you like */}
              </motion.button>
            </>
          )}
        </AnimatePresence>

        {/* Chat Window */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
              className="ev91-chat-window fixed bottom-8 right-4 z-[9999] w-[370px] max-w-full h-[540px] rounded-[1.75rem] overflow-hidden flex flex-col
                sm:w-[370px] sm:h-[540px]
                w-[95vw] h-[70vh]"
              style={{
                backdropFilter: "blur(8px)",
              }}
            >
              {/* Back Button - top left */}
              <button
                onClick={() => {
                  resetConversation();
                }}
                className="absolute left-4 top-4 z-20 rounded-full bg-white/12 px-3 py-1.5 text-xs font-bold text-lime-100 shadow hover:bg-white/20 transition"
                aria-label="Go Back"
              >
                Back
              </button>
              {/* Header */}
              <div className="ev91-chat-header flex items-center justify-between px-6 py-4 border-b border-white/10 text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <div className="flex items-center gap-4 relative z-10">
                  <div className="w-11 h-11 rounded-2xl bg-lime-300 text-slate-950 backdrop-blur-sm flex items-center justify-center font-black shadow-inner">
                    EV
                  </div>
                  <div>
                    <div className="text-base font-black">EV91 Assistant</div>
                    <div className="text-xs text-lime-100">Mobility helpdesk</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    resetConversation();
                    setOpen(false);
                  }}
                  className="relative z-10 hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  ✖
                </button>
              </div>

              {/* Body */}
              <div className="ev91-chat-body flex-1 flex flex-col-reverse overflow-y-auto p-6 space-y-4 space-y-reverse">
                {/* Render chat messages here, newest at the bottom */}
                {messages.map((msg, idx) => (
                  <div key={idx} className={`mb-2 ${msg.isUser ? "text-right" : "text-left"}`}>
                    <div className={`inline-block px-4 py-2 rounded-2xl shadow ${msg.isUser ? "bg-slate-950 text-white" : "bg-white text-emerald-950 font-semibold border border-emerald-100"}`}>
                      {msg.text}
                    </div>
                  </div>
                ))}

                {/* Warm Welcome Message */}
                {showWelcome && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="bg-white/95 p-5 rounded-2xl shadow text-center border border-emerald-100"
                  >
                    <div className="text-3xl mb-2 animate-bounce">👋</div>
                    <div className="text-lg font-bold text-emerald-800 mb-1">
                      Welcome to EV91!
                    </div>
                    <div className="text-sm text-emerald-700">
                      We're thrilled to help you on your green journey.<br />
                      <span className="font-semibold text-green-600">Did you know?</span> Every chat here plants a virtual tree! 🌱
                    </div>
                  </motion.div>
                )}

                {/* Show options only after welcome */}
                {!showWelcome && stage === "intro" && (
                  <div className="space-y-3">
                    <div className="bg-green-50 p-3 rounded-lg text-sm">
                      👋 Hi there! I’m <b>EV91’s Smart Assistant</b>.  
                      What would you like to know today?
                    </div>
                    <div className="grid gap-2">
                      {mainOptions.map((opt) => (
                        <motion.button
                          key={opt.id}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => handleSelectMain(opt.id)}
                          className="w-full text-left px-4 py-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-white to-emerald-50/50 hover:from-emerald-50/80 hover:to-emerald-100/50 hover:text-emerald-700 transition-all duration-300 shadow-sm hover:shadow group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-100/50 group-hover:bg-emerald-100 transition-colors">
                              {/* Add relevant icon here */}
                            </div>
                            <span className="font-medium">{opt.title}</span>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {stage === "followup" && currentFollowUp && (
                  <div className="space-y-4">
                    <div className="text-sm font-medium text-gray-700">
                      {typing ? (
                        <div className="flex items-center gap-2">
                          <div className="bg-emerald-100 rounded-full px-2 py-1 flex gap-1">
                            <span className="animate-bounce">.</span>
                            <span className="animate-bounce delay-150">.</span>
                            <span className="animate-bounce delay-300">.</span>
                          </div>
                        </div>
                      ) : (
                        currentFollowUp.q
                      )}
                    </div>

                    {!typing && currentFollowUp.type === "options" && (
                      <div className="grid gap-2">
                        {currentFollowUp.options.map((op) => (
                          <motion.button
                            whileTap={{ scale: 0.97 }}
                            key={op}
                            onClick={() => handleAnswer(currentFollowUp.id, op)}
                            className="px-3 py-2 rounded-lg border border-emerald-300 text-left hover:bg-emerald-100 hover:text-emerald-700 transition"
                          >
                            {op}
                          </motion.button>
                        ))}
                      </div>
                    )}

                    {!typing && currentFollowUp.type === "text" && (
                      <TextInput
                        placeholder="Type your answer..."
                        onSubmit={(val) => handleAnswer(currentFollowUp.id, val)}
                        openForm={openForm}
                        showFormButton={selectedMain !== "other"} // <--- Only show for non-"other"
                      />
                    )}
                  </div>
                )}

                {stage === "done" && (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <div className="text-sm font-semibold text-emerald-800">
                        ✅ Thanks for the info!
                      </div>
                      <div className="text-sm mt-1 text-gray-700">
                        Please fill this quick form so our team can reach you soon.
                      </div>
                    </div>

                    <div className="text-sm border rounded-lg p-3 bg-white/60">
                      {Object.entries(responses).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-gray-600">
                          <span className="font-semibold">{k}:</span> {v}
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button onClick={openForm} className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold hover:shadow-lg transition">Open Google Form</button>
                      <button onClick={() => { resetConversation(); setOpen(false); }} className="px-3 py-2 rounded-lg border">Close</button>
                    </div>
                  </div>
                )}

                {stage === "impress" && selectedMain === "investor" && (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 p-3 rounded-lg">
                      <div className="text-lg font-bold text-emerald-800 mb-2">
                        🚀 Why Invest in EV91?
                      </div>
                      <ul className="list-disc pl-5 text-emerald-900 text-sm mb-2">
                        <li>India’s fastest-growing EV logistics & rental platform</li>
                        <li>Proven business model with 10x growth potential</li>
                        <li>Backed by sustainable technology & real-world impact</li>
                        <li>Transparent operations and strong partner network</li>
                        <li>Be part of India’s green mobility revolution!</li>
                      </ul>
                      <div className="text-emerald-700 font-semibold mt-2">
                        Ready to join our journey and make a difference?
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          sendEnquiryEmail("Investor/Franchise Enquiry", responses);
                          setStage("thanks");
                        }}
                        className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold hover:shadow-lg transition"
                      >
                        Yes, I'm Interested!
                      </button>
                      <button
                        onClick={() => { resetConversation(); setOpen(false); }}
                        className="px-3 py-2 rounded-lg border"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}

                {stage === "thanks" && (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 p-3 rounded-lg text-center">
                      <div className="text-2xl mb-2">🙏</div>
                      <div className="text-lg font-semibold text-emerald-800">
                        Thank you for your interest in EV91!
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        Our team will reach out to you soon with exciting investment opportunities.
                      </div>
                    </div>
                    <button
                      onClick={() => { resetConversation(); setOpen(false); }}
                      className="w-full px-3 py-2 rounded-lg border"
                    >
                      Close
                    </button>
                  </div>
                )}

                {selectedMain === "other" && stage === "followup" && (
                  <FAQSection />
                )}

                {/* Scroll to bottom ref */}
                <div ref={messagesEndRef} />
              </div>

              {/* Footer */}
                <div className="px-4 py-3 border-t border-emerald-100 text-xs text-slate-500 bg-white/80 backdrop-blur-md text-center">
                Powered by <b>EV91</b> | Sustainable Mobility
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function TextInput({ placeholder, onSubmit = () => {}, openForm, showFormButton = true }) {
  const [val, setVal] = useState("");
  const [showResponse, setShowResponse] = useState(false);

  const handleSubmit = () => {
    if (!val.trim()) return;
    setShowResponse(true);
    onSubmit(val.trim());
    setVal("");
    setTimeout(() => setShowResponse(false), 3000);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 rounded-lg border border-emerald-300 focus:ring-2 focus:ring-emerald-200 outline-none"
        />
        <button
          onClick={handleSubmit}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
        >
          Send
        </button>
      </div>
      {showResponse && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="bg-emerald-50 p-3 rounded-lg text-sm"
        >
          <div className="font-medium text-emerald-800">
            {FAQ_DATA.responses.text_response}
          </div>
          {/* Only show the form button if showFormButton is true */}
          {showFormButton && (
            <button
              onClick={openForm}
              className="mt-2 w-full px-3 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white text-sm font-medium hover:shadow-lg transition-all duration-300"
            >
              Continue to Form →
            </button>
          )}
        </motion.div>
      )}
    </div>
  );
}

function FAQSection() {
  const [activeIdx, setActiveIdx] = useState(null);

  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-emerald-800 mb-2">
        💬 Ask us anything! Or select a question below:
      </div>
      <input
        type="text"
        value={activeIdx !== null ? FAQ_LIST[activeIdx].q : ""}
        onChange={(e) => {
          const val = e.target.value.toLowerCase();
          const idx = FAQ_LIST.findIndex(faq => faq.q.toLowerCase().includes(val));
          setActiveIdx(idx !== -1 ? idx : null);
        }}
        placeholder="Type your question..."
        className="w-full px-3 py-2 rounded-lg border border-emerald-300 focus:ring-2 focus:ring-emerald-200 outline-none"
      />
      <div className="grid gap-2">
        {FAQ_LIST.map((faq, idx) => (
          <div key={idx}>
            <button
              onClick={() => setActiveIdx(idx)}
              className={`w-full text-left px-3 py-2 rounded-lg border border-emerald-200 bg-white hover:bg-emerald-50 hover:text-emerald-700 transition`}
            >
              {faq.q}
            </button>
            {activeIdx === idx && (
              <div className="bg-emerald-50 p-3 rounded-lg text-sm font-medium text-emerald-900 mt-2">
                {faq.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function sendEnquiryEmail(subject, data) {
  emailjs.send(
    'service_c91jhm4',      // Replace with your EmailJS service ID
    'template_ny2auo1',     // Replace with your EmailJS template ID
    {
      subject,
      message: JSON.stringify(data, null, 2),
      to_email: 'sanjureddyb7@gmail.com'
    },
    'AW0Rg6kwuRdlh0SBL'          // Replace with your EmailJS user ID
  ).then(
    (result) => {
      // Optionally show a success message
    },
    (error) => {
      // Optionally show an error message
    }
  );
}


